<template>
  <div id="app">
    <RouterView />

    <div class="notifications-container">
      <PatientNotification
        v-for="(notification, index) in patientNotifications"
        :key="notification.id"
        :data="notification"
        @close="removeNotification(index)"
      />

      <AppointmentNotification
        v-for="(notification, index) in appointmentNotifications"
        :key="notification.id"
        :data="notification"
        @close="removeAppointmentNotification(index)"
      />
    </div>

    <Toasts
      :rtl="true"
      :max-messages="3"
      :showProgress="true"
      :time-out="4500"
      :closeable="true"
    />
  </div>
</template>

<script>
import axios from "axios"
import router from "@/router"
import appConfig from "@/app.config"
import PatientNotification from "@/components/PatientNotification.vue"
import AppointmentNotification from "@/components/AppointmentNotification.vue"

// axios.defaults.baseURL = "http://127.0.0.1:8000/api"
axios.defaults.baseURL = "https://admin.afrimedsolutions.cloud/api"

const token = localStorage.getItem("token")
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
}

export default {
  name: "App",
  components: {
    PatientNotification,
    AppointmentNotification,
  },
  data() {
    return {
      patientNotifications: [],
      appointmentNotifications: [],
    }
  },
  created() {
    let isHandling401 = false;
    axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && !isHandling401) {
          isHandling401 = true;
          this.$toast.error("Session expired. Please log in again.", { timeout: 3000 });
          setTimeout(() => {
            this.logout();
            isHandling401 = false;
          }, 5000);
        }
        return Promise.reject(error);
      }
    );

    const user = JSON.parse(localStorage.getItem("user"))

    const availableFonts = [
      "Poppins",
      "Arial",
      "Helvetica",
      "Georgia",
      "Times New Roman",
      "Verdana",
      "Tahoma",
      "Trebuchet MS",
      "Courier New",
      "Segoe UI",
    ];

    const savedFont = user?.settings?.default_font;
    const font = availableFonts.includes(savedFont) ? savedFont : "Poppins";
    const fontFamily = `"${font}", sans-serif`;

    document.documentElement.style.setProperty(
      "--bs-font-sans-serif",
      fontFamily
    );
    document.documentElement.style.setProperty(
      "--bs-body-font-family",
      fontFamily
    );
    
    if (!user) return

    this.$echo
      .private(`business.${user.businessID}.patients`)
      .listen(".new.patient", (e) => {
        this.patientNotifications.push(e)
        this.registerPatientNotification(e)
      })

    if (Number(user?.permissions?.appointment_manager || 0) === 1) {
      this.$echo
        .private(`business.${user.businessID}.appointments`)
        .listen(".appointment.module", (event) => {
          this.pushAppointmentNotification(event)
        })
    }

    this.$echo
      .private(`user.${user.id}.appointments`)
      .listen(".appointment.module", (event) => {
        this.pushAppointmentNotification(event)
      })
  },
  methods: {
    registerPatientNotification(notification) {
      const timestamp = notification.created_at || new Date().toISOString()
      const patientName = notification.name || "Unknown patient"
      const patientId = notification.patient_id || "N/A"

      this.$store.dispatch("notificationCenter/addNotification", {
        id: `patient-${notification.id || timestamp}`,
        type: "info",
        title: "New Patient Registered",
        message: `${patientName} was registered under this business.`,
        details: [
          `Patient ID: ${patientId}`,
          notification.gender ? `Gender: ${notification.gender}` : "",
          notification.telephone ? `Telephone: ${notification.telephone}` : "",
          notification.address ? `Address: ${notification.address}` : "",
        ].filter(Boolean),
        route: "/viewpatient",
        read: false,
        createdAt: timestamp,
      })
    },
    pushAppointmentNotification(event) {
      const payload = {
        id: `appointment-${event.created_at || Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: event.title || "Appointment Update",
        message: event.message || "There is a new appointment module update.",
        details: Array.isArray(event.details) ? event.details : [],
        created_at: event.created_at || new Date().toISOString(),
      }

      this.appointmentNotifications.push(payload)
      this.$toast.send(payload.message, event.type || "info", {
        recordInCenter: false,
      })
      this.$store.dispatch("notificationCenter/addNotification", {
        id: payload.id,
        type: event.type || "info",
        title: payload.title,
        message: payload.message,
        details: payload.details,
        route: event.route || "/appointments/settings",
        read: false,
        createdAt: payload.created_at,
      })
    },
    removeNotification(index) {
      this.patientNotifications.splice(index, 1)
    },
    removeAppointmentNotification(index) {
      this.appointmentNotifications.splice(index, 1)
    },
    logout() {
      localStorage.removeItem("token")
      localStorage.removeItem("user")
      delete axios.defaults.headers.common["Authorization"]
      router.push("/")
    },
  },
  page: {
    titleTemplate(title) {
      title = typeof title === "function" ? title(this.$store) : title
      return title ? `${title} | ${appConfig.title}` : appConfig.title
    },
  },
  beforeUnmount() {
    const user = JSON.parse(localStorage.getItem("user") || "null")
    if (!user) {
      return
    }

    this.$echo.leave(`business.${user.businessID}.patients`)
    this.$echo.leave(`business.${user.businessID}.appointments`)
    this.$echo.leave(`user.${user.id}.appointments`)
  }
}
</script>

<style scoped>
.notifications-container {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 360px;
  display: flex;
  flex-direction: column;
  gap: 10px; 
  z-index: 9999;
}
</style>
